import type {Editor} from 'grapesjs';
import {
  endVersionHistorySession,
  isVersionHistorySessionActive,
  previewVersion as previewVersionOnCanvas,
  revertToCurrentVersion,
  startVersionHistorySession,
} from '@Libraries/email-template-version-history.library';
import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {getMainWrapper} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {addReadOnlyFooter} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {saveEmailTemplateToCache, setEmailMakerAutosaveEnabled} from '@EmailMakerReact/lib/email-maker-storage-library';

/** Starts a version-history session: turns autosave off, anchors the undo baseline + redo tail, and locks the canvas read-only. */
export function enterVersionHistoryMode(editor: Editor): void {
  setEmailMakerAutosaveEnabled(editor, false);
  startVersionHistorySession(editor);
}

/**
 * Swaps the canvas to a stored version for viewing, resolving the React maker's wrapper handles.
 */
export function previewVersion(editor: Editor, jsonContent: string): void {
  previewVersionOnCanvas(editor, jsonContent, {
    wrapperType: EMAIL_MAKER_COMPONENT_TYPES.container.PMW_WRAPPER,
    getMainWrapper,
  });
}

/** Persists the just-restored content: it was applied with autosave off, so force one cache store before exiting. */
export async function persistRestoredTemplate(editor: Editor): Promise<void> {
  const stored = await saveEmailTemplateToCache(editor);
  if (stored === null) {
    throw new Error('persistRestoredTemplate: failed to save restored email template to cache');
  }
}

/**
 * Tears down version-history mode: reverts any unrestored preview, reconciles the redo stack, unlocks
 * the canvas, re-adds the read-only footer, re-selects the wrapper, and turns autosave back on.
 */
export async function exitVersionHistoryMode(editor: Editor): Promise<void> {
  if (!isVersionHistorySessionActive(editor)) {
    return;
  }

  revertToCurrentVersion(editor);
  endVersionHistorySession(editor);
  await addReadOnlyFooter(editor);

  const wrapper = getMainWrapper(editor);
  if (wrapper) {
    editor.select(wrapper);
  }

  setEmailMakerAutosaveEnabled(editor, true);
}
